import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/site/Logo";

const links = [
  { to: "/#why", label: "Why PLST" },
  { to: "/#who", label: "Who PLST is for" },
  { to: "/#process", label: "The PLST Process" },
  { to: "/#services", label: "Services" },
  { to: "/#team", label: "Team" },
  { to: "/#faq", label: "FAQs" },
  { to: "/#contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F5F4EF]/85 backdrop-blur-xl border-b border-[#1E1E1E]/10"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="PLST — home">
          <Logo variant="barbell" size={22} />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.to}
              href={l.to}
              data-testid={`nav-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="text-sm text-[#1E1E1E]/80 hover:text-[#1E1E1E] link-underline"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/book"
            data-testid="nav-book-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-[#1E1E1E] text-[#F5F4EF] px-5 py-2.5 text-sm font-medium hover:bg-[#A67C00] transition-colors duration-500"
          >
            Book an Assessment
            <span aria-hidden>→</span>
          </Link>
          <button
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-[#1E1E1E]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
            className="lg:hidden overflow-hidden bg-[#F5F4EF] border-t border-[#1E1E1E]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a key={l.to} href={l.to} className="text-base text-[#1E1E1E]/90">
                  {l.label}
                </a>
              ))}
              <Link to="/book" className="mt-2 bg-[#1E1E1E] text-[#F5F4EF] px-5 py-3 text-sm inline-flex justify-between">
                Book an Assessment <span>→</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
